import { SystemSetting } from '@/api/managers/SystemSetting';
import { getApiHost } from '@/appSettings';
import { useResgate } from '@/login/composables/useResgate';
import { useSettings } from '@/login/composables/useSettings';
import { useLoginStore } from '@/login/stores/useLoginStore';
import type { BetaFlag } from '@sysarb/sysarb-types/services/v1/authentication';
import { betaFlagUpdateValidator } from '@sysarb/sysarb-types/services/v1/authentication';
import { defineStore, storeToRefs } from 'pinia';
import type { ComputedRef } from 'vue';
import { computed, onUnmounted } from 'vue';

const availableBetaFlags = [
    'CostToFix',
    'AnalyzeV2',
    'SCIM',
] as const;

const availableAlphaFlags = [
    'agentChat',
    'analyzeBasedOnFriends',
    'automatedAnalysis',
    'partnerPlatform',
    'additionalSalaryBenefits',
    'employmentInsights',
    'aiReport',
    'costToFixV2',
    'apiKeyManagement',
    'statutoryGradeInvolve'
] as const;

// Alpha flags that work for all customers (not just support/dev)
export const customerAlphaFlags: readonly AlphaFlagValue[] = ['analyzeBasedOnFriends'];

type LabsReturn = {
    activeFlags: ComputedRef<Flag[]>;
    alphaFlags: ComputedRef<BetaFlag[]>;
    betaFlags: ComputedRef<BetaFlag[]>;
    toggleLabs: () => Promise<void>;
    toggleFlag: (flag: Flag) => Promise<void>;
    flagsActivated: ComputedRef<boolean>;
    availableAlphaFlags: readonly AlphaFlagValue[];
    availableBetaFlags: readonly BetaFlagValue[];
    activateAlpha: ComputedRef<boolean>;
    activateBeta: ComputedRef<boolean>;
};

type AlphaFlagValue = typeof availableAlphaFlags[number];
type BetaFlagValue = typeof availableBetaFlags[number];

export type Flag = AlphaFlagValue | BetaFlagValue;

export const useLabsStore = defineStore('labsStore', () => {
    const loginStore = useLoginStore();
    const { selectedInstance } = storeToRefs(loginStore);
    const { isSupport } = storeToRefs(useLoginStore());
    const isDev = import.meta.env.DEV === true;

    const activeFlags = computed(() => {
        if (selectedInstance.value?.flags == null) return [];
        if (selectedInstance.value.settings?.ActivateBetaFeatures !== true) return [];

        if (isSupport.value || isDev) {
            return selectedInstance.value.flags.flags.filter(flag => flag.enabled && [...availableBetaFlags, ...availableAlphaFlags].includes(flag.key as Flag)).map(flag => flag.key as Flag);
        }

        return selectedInstance.value.flags.flags.filter(flag => flag.enabled && (availableBetaFlags.includes(flag.key as BetaFlagValue) || customerAlphaFlags.includes(flag.key as typeof customerAlphaFlags[number]))).map(flag => flag.key as Flag);
    });

    return {
        activeFlags
    };
});

export function useLabs(): LabsReturn {
    const { flags, instanceSettings } = useSettings();
    const resgate = useResgate(crypto.randomUUID());
    const { instance, instanceTopic, isSupport, isDev, userId } = storeToRefs(useLoginStore());
    const systemSettingRest = new SystemSetting(instance.value, getApiHost());

    const activateBeta = computed<boolean>((): boolean => {
        const result = instanceSettings.value?.ActivateBetaFeatures ?? false;

        if (result) {
            console.info({ cat: 'labs', msg: 'Beta features activated' });
        }

        return result;
    });

    /**
     * Returns if alpha should be activated
     * The requisites for this is that
     *      1. Beta is activated
     *      2.  - Either we are in local development
     *          - In the dev enviroment
     *          - The logged in user is a support user
     */
    const activateAlpha = computed<boolean>((): boolean => {
        if (!activateBeta.value) return false;

        const result = isSupport.value || isDev.value;

        if (result) {
            console.info({ cat: 'labs', msg: 'Alpha features activated' });
        }

        return result;
    });

    const activeFlags = computed(() => {
        if (!activateBeta.value) return [];
        let enabledFlags: BetaFlag[] = [];

        if (activateAlpha.value) {
            enabledFlags = flags.value?.filter(flag => flag.enabled && [...availableBetaFlags, ...availableAlphaFlags].includes(flag.key as Flag)) ?? [];
        } else {
            enabledFlags = flags.value?.filter(flag => flag.enabled && (availableBetaFlags.includes(flag.key as BetaFlagValue) || customerAlphaFlags.includes(flag.key as typeof customerAlphaFlags[number]))) ?? [];
        }

        return enabledFlags.map(flag => flag.key as Flag);
    });

    const betaFlags = computed(() => flags.value?.filter(flag => availableBetaFlags.includes(flag.key as BetaFlagValue)) ?? []);

    const alphaFlags = computed(() => flags.value?.filter(flag => availableAlphaFlags.includes(flag.key as AlphaFlagValue)) ?? []);

    async function activateLabs(): Promise<void> {
        await systemSettingRest.updateFieldValue('ActivateBetaFeatures', true);
    }

    async function deactivateLabs(): Promise<void> {
        await systemSettingRest.updateFieldValue('ActivateBetaFeatures', false);
    }

    async function toggleLabs(): Promise<void> {
        if (activateBeta.value) {
            await deactivateLabs();
        } else {
            await activateLabs();
        }
    }

    async function toggleFlag(flag: Flag): Promise<void> {
        if (userId == null) return;
        if (instanceTopic.value == null) return;

        const enabled = activeFlags.value.includes(flag);
        const input = betaFlagUpdateValidator.safeParse({ key: flag, enabled: !enabled });

        if (input.success) {
            await resgate.call(`v1.instance.${instanceTopic.value}.flags`, 'update-domain', input.data);
        } else {
            console.error({ cat: 'labs', msg: 'Invalid payload', error: input.error });
        }
    }

    const flagsActivated = computed(() => activateAlpha.value || activateBeta.value);

    onUnmounted(() => {
        resgate.unsubscribe();
        systemSettingRest.abort();
    });

    return {
        activeFlags,
        activateAlpha,
        activateBeta,
        alphaFlags,
        betaFlags,
        toggleLabs,
        toggleFlag,
        flagsActivated,
        availableAlphaFlags,
        availableBetaFlags
    };
}
